import { createSignal, createResource, getOwner, onCleanup, createEffect, Show, on } from 'solid-js';
import { Select } from '@thisbeyond/solid-select';
import { twclsx, apiFetch, Icons } from '../shared';

export interface MasterCategory {
  id: string;
  name: string;
}

export type MasterCategoryType = 'product-classes' | 'account-categories';

interface MasterCategorySelectProps {
  id: string;
  label?: string;
  name: string;
  entityId: string;
  class?: string;
  placeholder?: string;
  types: MasterCategoryType[];
  initialValue?: string;
  format: (master: MasterCategory) => string;
  onChange: (master: MasterCategory) => void;
  required?: boolean;
  createNew?: () => void;
}

export const [refreshOptions, setMasterCategorySelectRefreshOptions] = createSignal(undefined, { equals: false });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const throttle = (callback: (...args: any[]) => Promise<unknown>, threshold: number) => {
  let activePromise: Promise<unknown> | null = null;
  let timeoutId: ReturnType<typeof setTimeout>;
  let lastArgs: Parameters<typeof callback>;

  // eslint-disable-next-line no-return-assign, no-promise-executor-return
  const wait = () => new Promise(resolve => (timeoutId = setTimeout(resolve, threshold)));

  const throttled: typeof callback = (...args) => {
    lastArgs = args;
    if (activePromise) {
      return activePromise;
    }

    activePromise = wait().then(() => {
      activePromise = null;
      return callback(...lastArgs);
    });

    return activePromise;
  };

  const clear = () => {
    clearTimeout(timeoutId);
    activePromise = null;
  };

  if (getOwner()) {
    onCleanup(clear);
  }

  return Object.assign(throttled, { clear });
};

// TODO: Implement caching for master categories

const MasterCategorySelect = (props: MasterCategorySelectProps, timeout = 500) => {
  const [inputValue, setInputValue] = createSignal('');
  const [initialValue, setInitialValue] = createSignal<MasterCategory | undefined>();
  const selectProps = async ([types, name]: [MasterCategoryType[], string]) => {
    const results = await Promise.all(
      types.map(async type => {
        const category = await (
          await apiFetch(`/entities/${props.entityId}/${type}?${new URLSearchParams({ name })}`)
        ).json();

        return category;
      }),
    );

    return results.flat();
  };

  const throttledFetcher = throttle(selectProps, timeout);
  const [options, { refetch }] = createResource(() => [props.types, inputValue()] as const, throttledFetcher, {
    initialValue: [],
  });

  createEffect(() => {
    if (props.initialValue && options.state === 'ready') {
      setInitialValue((options() as Array<MasterCategory>).find((o: MasterCategory) => o.id === props.initialValue!));
    }

    if (typeof props.initialValue !== 'string' && props.initialValue && !Object.keys(props.initialValue).length) {
      setInitialValue({} as MasterCategory);
    }

    if (typeof props.initialValue === 'string' && props.initialValue === '') {
      setInitialValue({} as MasterCategory);
    }
  });

  createEffect(
    on(refreshOptions, () => {
      refetch();
    }),
  );

  return (
    <div>
      <Show when={props.label}>
        <label
          for={props.id}
          class={twclsx('block', 'mb-2', 'text-sm', 'font-medium', 'text-gray-900', 'dark:text-white')}
        >
          {props.label}
        </label>
      </Show>

      <div class="flex items-center">
        <Select
          format={props.format}
          placeholder={props.placeholder}
          class={twclsx(
            'block',
            'w-full',
            'p-2.5',
            'border',
            'text-sm',
            props.createNew ? 'rounded-s-lg' : 'rounded-lg',
            'bg-gray-50',
            'border-gray-300',
            'text-gray-900',
            'focus:ring-blue-500',
            'focus:border-blue-500',
            'dark:bg-gray-700',
            'dark:border-gray-600',
            'dark:placeholder-gray-400',
            'dark:text-white',
            'dark:focus:ring-blue-500',
            'dark:focus:border-blue-500',
            props.class,
          )}
          onChange={props.onChange}
          initialValue={initialValue()}
          onInput={setInputValue}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          options={options() as any[]}
          loading={options.loading}
          readonly={false}
        />

        <Show when={props.createNew}>
          <button
            type="button"
            class={twclsx(
              'inline-flex',
              'items-center',
              'justify-center',
              'p-1',
              'text-sm',
              'font-medium',
              'text-center',
              'text-white',
              'rounded-e-lg',
              'bg-primary-700',
              'hover:bg-primary-800',
              'focus:ring-4',
              'focus:ring-primary-300',
              'dark:bg-primary-600',
              'dark:hover:bg-primary-700',
              'dark:focus:ring-primary-800',
            )}
            onClick={() => {
              props.createNew?.();
            }}
          >
            <Icons.Plus class={twclsx('w-8', 'h-8', 'text-white')} />
          </button>
        </Show>
      </div>
    </div>
  );
};

export default MasterCategorySelect;
